import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import DOMPurify, { type Config } from "dompurify";

/** Пропсы компонента {@link Svg}. */
export interface SvgProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * URL SVG-файла или raw SVG-разметка. \
     * Raw-разметка перед вставкой в DOM проходит SVG-only sanitizer.
     * @example "/images/common/plus.svg"
     * @example "<svg>...</svg>"
     */
    src: string;
    /**
     * Доступное имя для screen readers.
     */
    alt?: string;
    /**
     * Режим прямого рендера SVG-разметки из {@link SvgProps.src src}. \
     * Разметка санитайзится перед вставкой в DOM.
     * @default false
     */
    raw?: boolean;
    /**
     * Колбэк после успешной загрузки или обработки SVG.
     */
    onLoad?: () => void;
}

interface SvgCacheEntry {
    data?: string;
    error?: unknown;
    promise?: Promise<string>;
    timestamp?: number;
    gcTimer?: ReturnType<typeof setTimeout>;
    lastAccessed: number;
}

const STALE_TIME = 86_400_000;
const CACHE_TIME = 60_000;
const svgCache = new Map<string, SvgCacheEntry>();

/** Настройки sanitizer: разрешаем только безопасный SVG/SVG filters, без HTML и MathML. */
const SVG_SANITIZE_CONFIG: Config = {
    USE_PROFILES: { svg: true, svgFilters: true },
};

const isEmptyString = (value: string): boolean => value.trim().length === 0;
const resolveSvgUrl = (src: string): string => (src.startsWith("/") ? `${import.meta.env.BASE_URL}${src.slice(1)}` : src);

/**
 * Очищает SVG-разметку перед вставкой через `dangerouslySetInnerHTML`.
 * @param markup исходная SVG-разметка
 */
function sanitizeSvgMarkup(markup: string): string {
    return DOMPurify.sanitize(markup, SVG_SANITIZE_CONFIG);
}

function resetGcTimer(key: string, entry: SvgCacheEntry): void {
    if (entry.gcTimer != null) {
        clearTimeout(entry.gcTimer);
    }

    entry.gcTimer = setTimeout(() => {
        const cachedEntry = svgCache.get(key);

        if (cachedEntry == null) {
            return;
        }

        if (Date.now() - cachedEntry.lastAccessed >= CACHE_TIME) {
            if (cachedEntry.gcTimer != null) {
                clearTimeout(cachedEntry.gcTimer);
            }

            svgCache.delete(key);
            return;
        }

        resetGcTimer(key, cachedEntry);
    }, CACHE_TIME);
}

function getFreshCachedSvg(src: string): string | undefined {
    const entry = svgCache.get(src);

    if (entry == null || entry.data == null || entry.timestamp == null) {
        return undefined;
    }

    entry.lastAccessed = Date.now();
    resetGcTimer(src, entry);

    return Date.now() - entry.timestamp < STALE_TIME ? entry.data : undefined;
}

async function fetchSvgWithCache(src: string): Promise<string> {
    const now = Date.now();
    let entry = svgCache.get(src);

    if (entry == null) {
        entry = { lastAccessed: now };
        svgCache.set(src, entry);
    } else {
        entry.lastAccessed = now;
        resetGcTimer(src, entry);
    }

    const cached = getFreshCachedSvg(src);
    if (cached != null) {
        return cached;
    }

    if (entry.promise != null) {
        return entry.promise;
    }

    entry.promise = fetch(src)
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`Failed to load SVG "${src}": ${response.status}`);
            }

            const data = sanitizeSvgMarkup(await response.text());
            const finishedEntry = svgCache.get(src) ?? entry;

            finishedEntry.data = data;
            finishedEntry.error = undefined;
            finishedEntry.promise = undefined;
            finishedEntry.timestamp = Date.now();
            finishedEntry.lastAccessed = Date.now();
            resetGcTimer(src, finishedEntry);

            return data;
        })
        .catch((error: unknown) => {
            const failedEntry = svgCache.get(src) ?? entry;

            failedEntry.error = error;
            failedEntry.promise = undefined;
            failedEntry.lastAccessed = Date.now();
            resetGcTimer(src, failedEntry);

            throw error;
        });

    resetGcTimer(src, entry);

    return entry.promise;
}

/**
 * Рендерит SVG из URL или raw-разметки без привязки к глобальным React-store.
 */
export const Svg: React.FC<SvgProps> = ({ className, src, alt, raw = false, onLoad, ...divProps }) => {
    const [data, setData] = useState<string | undefined>(undefined);
    const [isError, setIsError] = useState(false);
    const rawData = useMemo(() => (raw && !isEmptyString(src) ? sanitizeSvgMarkup(src) : undefined), [raw, src]);
    const resolvedSrc = useMemo(() => resolveSvgUrl(src), [src]);

    const fallback = useMemo(
        () => <div {...divProps} className={classNames(className)} role="img" aria-label={alt ?? "SVG placeholder"} />,
        [alt, className, divProps],
    );

    useEffect(() => {
        if (raw) {
            setData(undefined);
            setIsError(false);

            if (rawData != null) {
                onLoad?.();
            }

            return;
        }

        if (isEmptyString(src)) {
            setData(undefined);
            setIsError(false);
            return;
        }

        const cached = getFreshCachedSvg(resolvedSrc);

        if (cached != null) {
            setData(cached);
            setIsError(false);
            onLoad?.();
            return;
        }

        let isMounted = true;
        setIsError(false);

        fetchSvgWithCache(resolvedSrc)
            .then((loadedSvg) => {
                if (!isMounted) {
                    return;
                }

                setData(loadedSvg);
                setIsError(false);
                onLoad?.();
            })
            .catch(() => {
                if (!isMounted) {
                    return;
                }

                setData(undefined);
                setIsError(true);
            });

        return () => {
            isMounted = false;
        };
    }, [onLoad, raw, rawData, resolvedSrc, src]);

    if (raw) {
        return rawData == null ? null : (
            <div
                {...divProps}
                className={classNames(className)}
                role="img"
                aria-label={alt}
                dangerouslySetInnerHTML={{ __html: rawData }}
            />
        );
    }

    if (isError) {
        return fallback;
    }

    if (data == null) {
        return null;
    }

    return (
        <div
            {...divProps}
            className={classNames(className)}
            role="img"
            aria-label={alt}
            dangerouslySetInnerHTML={{ __html: data }}
        />
    );
};
